import styled from "styled-components";

export const Container = styled.div`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #007DED 0%, #0056b3 100%);
  color: white;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
`;

export const Badges = styled.div`
  display: flex;
  gap: 8px;
`;

export const Badge = styled.span<{ variant: 'pendente' | 'expirado' | 'realizado' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  
  ${({ variant }) => {
    switch (variant) {
      case 'pendente':
        return `
          background-color: #fff3cd;
          color: #856404;
        `;
      case 'expirado':
        return `
          background-color: #f8d7da;
          color: #721c24;
        `;
      case 'realizado':
        return `
          background-color: #d4edda;
          color: #155724;
        `;
      default:
        return '';
    }
  }}
`;

export const Content = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(90vh - 120px);
`;

export const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #eee;
  margin-bottom: 16px;
`;

export const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ active }) => (active ? '#007DED' : '#666')};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${({ active }) => (active ? '#007DED' : 'transparent')};
    transition: background-color 0.2s ease;
  }

  &:hover {
    color: #007DED;
  }
`;

export const ItemCard = styled.div<{ status: string }>`
  background: #f9f9f9;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid ${({ status }) => {
    switch (status) {
      case 'pendente':
        return '#ffc107';
      case 'expirado':
        return '#dc3545';
      case 'realizado':
        return '#28a745';
      default:
        return '#6c757d';
    }
  }};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

export const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

export const ItemTitle = styled.div`
  h4 {
    margin: 0 0 4px 0;
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  span {
    font-size: 12px;
    color: #666;
  }
`;

export const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    switch (status) {
      case 'pendente':
        return `
          background-color: #fff3cd;
          color: #856404;
        `;
      case 'expirado':
        return `
          background-color: #f8d7da;
          color: #721c24;
        `;
      case 'realizado':
        return `
          background-color: #d4edda;
          color: #155724;
        `;
      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

export const ItemInfo = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;

  p {
    margin: 4px 0;
  }

  strong {
    color: #333;
  }
`;

export const AlertBadge = styled.div<{ tipo: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin-bottom: 10px;
  
  ${({ tipo }) => {
    switch (tipo) {
      case 'danger':
        return `
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'warning':
        return `
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeeba;
        `;
      case 'info':
        return `
          background-color: #d1ecf1;
          color: #0c5460;
          border: 1px solid #bee5eb;
        `;
      default:
        return '';
    }
  }}
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ActionButton = styled.button<{ variant?: 'primary' | 'success' | 'danger' | 'outline' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background-color: #007DED;
          color: white;
          border: none;
          &:hover { background-color: #0056b3; }
        `;
      case 'success':
        return `
          background-color: #28a745;
          color: white;
          border: none;
          &:hover { background-color: #218838; }
        `;
      case 'danger':
        return `
          background-color: #dc3545;
          color: white;
          border: none;
          &:hover { background-color: #c82333; }
        `;
      case 'outline':
      default:
        return `
          background-color: transparent;
          color: #007DED;
          border: 1px solid #007DED;
          &:hover { background-color: #f0f7ff; }
        `;
    }
  }}
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #666;

  svg {
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

export const HistoryTimeline = styled.div`
  position: relative;
  padding-left: 24px;

  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #e0e0e0;
  }
`;

export const HistoryItem = styled.div<{ status: string }>`
  position: relative;
  padding-bottom: 20px;

  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ status }) => {
      switch (status) {
        case 'realizado':
          return '#28a745';
        case 'cancelado':
          return '#dc3545';
        case 'expirado':
          return '#dc3545';
        default:
          return '#ffc107';
      }
    }};
    border: 2px solid white;
    box-shadow: 0 0 0 2px ${({ status }) => {
      switch (status) {
        case 'realizado':
          return '#28a745';
        case 'cancelado':
          return '#dc3545';
        case 'expirado':
          return '#dc3545';
        default:
          return '#ffc107';
      }
    }};
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

export const HistoryContent = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 12px;

  h5 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  span {
    font-size: 12px;
    color: #666;
  }

  p {
    margin: 8px 0 0 0;
    font-size: 13px;
    color: #555;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
`;

export const ExpandedDetails = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin: 12px 0;
  overflow: hidden;
`;

export const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
`;

export const DetailsContent = styled.div`
  padding: 14px;
`;

export const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #333;
  border-bottom: 1px dashed #eee;

  &:last-child {
    border-bottom: none;
  }

  strong {
    color: #495057;
    min-width: 120px;
    flex-shrink: 0;
  }
`;

